<template>
  <div
    class="card"
    @click="sightStore.useSight(sight)"
  >
    <Img
      :src="sight.cover_photo?.thumbnail"
      width="88px"
      height="120px"
      :alt="sight.name"
      border-radius="8px"
    />
    <div class="text">
      <div
        ref="title"
        class="title"
      >
        {{ sight.name }}
      </div>
      <div
        ref="subtitle"
        :class="['subtitle', { hidden: viewMore }]"
      >
        {{ sight.description }}
        <div
          v-if="viewMore"
          class="more"
        >
          Ещё
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { useTemplateRef, computed } from 'vue';

  import { type CityTagsAdvanced } from '@/shared/models/types';
  import { useSightsStore } from '@/shared/store/sights';
  import Img from '@/shared/ui/ResponsiveImage.vue';

  const titleRef = useTemplateRef('title');
  const subtitleRef = useTemplateRef('subtitle');

  const cardHeight = 120;
  const lineHeight = 20;

  const sightStore = useSightsStore();

  const maxSubtitleHeight = computed(() => cardHeight - (titleRef.value?.offsetHeight ?? 0));
  const maxSubtitleColumn = computed(() => maxSubtitleHeight.value / lineHeight);
  const subtitleColumns = computed(() => Math.floor((subtitleRef.value?.scrollHeight ?? 0) / lineHeight));
  const viewMore = computed(() => subtitleColumns.value > maxSubtitleColumn.value);

  defineProps<{
    sight: CityTagsAdvanced;
  }>();
</script>

<style lang="scss" scoped>
  @use '@experience/design-tokens/typography' as legacy-typography;

  .card {
    display: flex;
    gap: 8px;
    width: 327px;
    padding: 8px;
    color: var(--alias-color-text-default);
    text-decoration: none;
    cursor: pointer;
    background: var(--alias-color-background-default);
    border-radius: var(--alias-radius-16, 16px);
    box-shadow: 0 0 18px 0 var(--global-color-transparent-black-a500);
  }

  .title {
    @include legacy-typography.base-400-300-semi;
  }

  .subtitle {
    position: relative;
    display: -webkit-box;
    overflow: auto;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: v-bind('maxSubtitleColumn');

    &:hover {
      .more {
        color: var(--alias-color-link-default-hovered);
        text-decoration-color: var(--alias-color-link-default-hovered);
      }
    }

    @include legacy-typography.base-300-300-regular;
  }

  .more {
    position: absolute;
    right: 0;
    bottom: 0;
    text-decoration-line: underline;
    text-decoration-style: solid;
    text-decoration-color: var(--alias-color-text-subtler);
    text-decoration-skip-ink: none;
    background-color: var(--alias-color-background-default);

    &::before {
      position: absolute;
      top: 0;
      left: -8px;
      width: 8px;
      height: 100%;
      content: '';
      background-color: var(--alias-color-background-default);
    }

    @include legacy-typography.base-300-300-regular;
  }

  .hidden {
    overflow: hidden;
  }
</style>
